import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { DisclosuresFilterBar } from './DisclosuresFilterBar';
import { DisclosuresTable } from './DisclosuresTable';
import { DisclosureDetailModal } from './DisclosureDetailModal';
import { Pager } from '../../components/Pager';
import { getDisclosureDetail, listDisclosures } from '../../api/disclosuresApi';
import {
  buildDefaultFilters,
  disclosureFiltersFromSearchParams,
  disclosureFiltersToQuery,
  disclosureFiltersToSearchParams,
  type DisclosureFilters,
} from './disclosures.types';
import '../SystemLog/systemLog.css';
import './disclosures.css';

// 권위: docs/ARCH_DASHBOARD.md §4.6 탭 6-A — 뉴스/공시 열람 화면 (/data/disclosures).
//
// 조회 전용 화면 — WebSocket 구독 없음. TanStack Query staleTime 30초.
// 필터 상태 = URL 쿼리스트링 (북마크/공유 가능).
// 상세 모달은 선택된 auditId 를 로컬 쿼리스트링 key `audit` 로 관리하여 딥링크 가능하게 한다.

const QK_DISCLOSURE_LIST = 'disclosures-list';
const QK_DISCLOSURE_DETAIL = 'disclosures-detail';

export default function DisclosuresPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  // 필터는 URL 쿼리스트링에서 단일 진실원.
  const filters = useMemo<DisclosureFilters>(
    () => disclosureFiltersFromSearchParams(searchParams),
    [searchParams]
  );

  const setFilters = useCallback(
    (next: DisclosureFilters) => {
      const sp = disclosureFiltersToSearchParams(next);
      // audit 쿼리 보존 (필터 변경으로 모달이 닫히지 않도록).
      const audit = searchParams.get('audit');
      if (audit) sp.set('audit', audit);
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const onReset = useCallback(() => {
    const sp = disclosureFiltersToSearchParams(buildDefaultFilters());
    setSearchParams(sp, { replace: true });
  }, [setSearchParams]);

  // ── 목록 조회 ────────────────────────────────────────
  const listQuery = useQuery({
    queryKey: [QK_DISCLOSURE_LIST, filters],
    queryFn: () => listDisclosures(disclosureFiltersToQuery(filters)),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;

  // ── 상세 모달 상태 (URL `audit` 쿼리) ───────────────
  const auditStr = searchParams.get('audit');
  const auditId = (() => {
    if (!auditStr) return null;
    const n = parseInt(auditStr, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();

  const detailQuery = useQuery({
    queryKey: [QK_DISCLOSURE_DETAIL, auditId],
    queryFn: () => getDisclosureDetail(auditId as number),
    enabled: auditId != null,
    staleTime: 30_000,
  });

  const openDetail = useCallback(
    (id: number) => {
      const sp = new URLSearchParams(searchParams);
      sp.set('audit', String(id));
      setSearchParams(sp, { replace: false });
    },
    [searchParams, setSearchParams]
  );

  const closeDetail = useCallback(() => {
    const sp = new URLSearchParams(searchParams);
    sp.delete('audit');
    setSearchParams(sp, { replace: false });
  }, [searchParams, setSearchParams]);

  return (
    <div className="syslog-page">
      <header className="syslog-page__header">
        <h1 className="syslog-page__title">뉴스 / 공시</h1>
        <div className="syslog-page__meta">
          총 {total.toLocaleString('ko-KR')} 건
          {listQuery.isFetching && <span className="syslog-muted"> · 갱신 중…</span>}
        </div>
      </header>

      <DisclosuresFilterBar filters={filters} onChange={setFilters} onReset={onReset} />

      {listQuery.isError && (
        <div className="syslog-warning-banner">
          목록 로드 실패: {(listQuery.error as Error | null)?.message ?? 'unknown'}
        </div>
      )}

      <DisclosuresTable
        items={items}
        isLoading={listQuery.isLoading}
        onRowClick={(it) => openDetail(it.auditId)}
      />

      <Pager
        page={filters.page}
        pageSize={filters.pageSize}
        total={total}
        onPageChange={(p) => setFilters({ ...filters, page: p })}
      />

      {auditId != null && (
        <DisclosureDetailModal
          detail={detailQuery.data ?? null}
          isLoading={detailQuery.isLoading}
          isError={detailQuery.isError}
          error={(detailQuery.error as Error | null) ?? null}
          onClose={closeDetail}
        />
      )}
    </div>
  );
}
